import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { getUserEmailFromCookie } from "../utils/cookieUtils";
import TeamFloatingNavigation from "../components/TeamFloatingNavigation";

function AddSalePage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [agentName, setAgentName] = useState("");
  const [selectedGuideId, setSelectedGuideId] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [numberOfTickets, setNumberOfTickets] = useState("1");
  const [ticketUnitPrice, setTicketUnitPrice] = useState(5000);
  const [customPrice, setCustomPrice] = useState("");
  const [isCustomPrice, setIsCustomPrice] = useState(false);
  const [comments, setComments] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  
  // Data state
  const [agentList, setAgentList] = useState([]);
  const [guideList, setGuideList] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [forcedSubmit, setForcedSubmit] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const totalPrice = (parseInt(numberOfTickets) || 0) * (isCustomPrice ? parseFloat(customPrice) || 0 : ticketUnitPrice);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user profile to get site-specific agents
        const userResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const userSiteId = userResponse.data.assignedSite?._id;
        
        // Fetch agents and guides in parallel
        const [agentsResponse, guidesResponse] = await Promise.all([
          userSiteId ? 
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/agents/${userSiteId}`, {
              headers: { Authorization: `Bearer ${token}` }
            }) : 
            Promise.resolve({ data: [] }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/get-guides`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setAgentList(agentsResponse.data || []);
        setGuideList(guidesResponse.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setAgentList([]);
        setGuideList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleTicketChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
      setNumberOfTickets(value);
    }
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === "custom") {
      setIsCustomPrice(true);
      setTicketUnitPrice(0);
    } else {
      setIsCustomPrice(false);
      setTicketUnitPrice(Number(value));
      setCustomPrice("");
    }
  };

  const handleCustomPriceChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setCustomPrice(value);
    }
  };

  // Check for potential duplicate sales
  const checkForDuplicates = async (saleData) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/check-duplicate-sale`,
        {
          salesAgent: saleData.salesAgent,
          numberOfTickets: saleData.numberOfTickets,
          totalPrice: saleData.totalPrice,
          customerType: saleData.customerType,
          bookingType: saleData.bookingType,
          guideId: saleData.guideId,
          timeWindow: 5 // Check for duplicates within 5 minutes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return response.data.isDuplicate ? response.data : null;
    } catch (error) {
      console.error("Error checking duplicates:", error);
      return null;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!agentName) newErrors.agentName = "Please select an agent";
    if (!bookingType) newErrors.bookingType = "Please select booking type";
    if (!paymentMethod) newErrors.paymentMethod = "Please select payment method";
    if (!customerType) newErrors.customerType = "Please select customer type";
    if (!numberOfTickets || parseInt(numberOfTickets) < 1) {
      newErrors.numberOfTickets = "Please enter at least 1 ticket";
    }
    if (bookingType === "Brought by Guide" && !selectedGuideId) {
      newErrors.guide = "Please select a guide";
    }
    if (isCustomPrice && (!customPrice || parseFloat(customPrice) <= 0)) {
      newErrors.customPrice = "Please enter a valid custom price";
    }
    if (isCustomPrice && !comments.trim()) {
      newErrors.comments = "Comments are required when using custom price";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);
    setErrors({});

    const selectedGuide = guideList.find((g) => g._id === selectedGuideId);
    
    // Get logged user's email from cookies
    const loggedUserEmail = getUserEmailFromCookie();

    const saleData = {
      salesAgent: agentName,
      bookingType,
      numberOfTickets: parseInt(numberOfTickets),
      totalPrice,
      paymentMethod,
      customerType,
      comments: comments.trim(),
      invoiceNumber: invoiceNumber.trim(),
      guideDetails: bookingType === "Brought by Guide" ? selectedGuide : {},
      guideId: bookingType === "Brought by Guide" ? selectedGuideId : null,
      guideName: bookingType === "Brought by Guide" && selectedGuide ? selectedGuide.guideName : null,
      // Add logged user's email to sale record
      createdByEmail: loggedUserEmail || user?.email || null,
      createdByRole: user?.role || null,
    };

    try {
      // Check for duplicates unless this is a forced submit
      if (!forcedSubmit) {
        const duplicateData = await checkForDuplicates(saleData);
        if (duplicateData) {
          setDuplicateWarning(duplicateData);
          setShowDuplicateModal(true);
          setSubmitting(false);
          return;
        }
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/add-sale`,
        saleData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show success modal
      setModalMessage("Sale submitted successfully!");
      setShowSuccessModal(true);

      // Reset form
      setAgentName("");
      setBookingType("");
      setPaymentMethod("");
      setCustomerType("");
      setNumberOfTickets("1");
      setTicketUnitPrice(5000);
      setCustomPrice("");
      setIsCustomPrice(false);
      setComments("");
      setInvoiceNumber("");
      setSelectedGuideId("");
      setErrors({});
      setForcedSubmit(false);
    } catch (error) {
      // Show error modal
      setModalMessage(error.response?.data?.message || "Failed to submit sale. Please try again.");
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle forced submission (ignore duplicates)
  const handleForceSubmit = () => {
    setForcedSubmit(true);
    setShowDuplicateModal(false);
    // Trigger form submission again
    const form = document.querySelector('form');
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  // Handle duplicate modal cancel
  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setDuplicateWarning(null);
    setForcedSubmit(false);
  };

  // Handle success modal close
  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    setModalMessage("");
  };

  // Handle error modal close
  const handleErrorClose = () => {
    setShowErrorModal(false);
    setModalMessage("");
    setErrors({});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="w-full max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
          <h1 className="text-2xl font-bold text-slate-800 text-center">Add Sale</h1>
          <p className="text-sm text-slate-500 text-center mt-1">Record a new ticket sale</p>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-6">
            {errors.submit}
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Row - Agent & Booking Type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sales Agent <span className="text-red-500">*</span>
                </label>
                <select
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                >
                  <option value="">Select an agent</option>
                  {agentList.map((agent) => (
                    <option key={agent._id} value={agent.name}>
                      {agent.name}
                    </option>
                  ))}
                </select>
                {errors.agentName && (
                  <p className="text-xs text-red-600 mt-1">{errors.agentName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Booking Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={bookingType}
                  onChange={(e) => setBookingType(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                >
                  <option value="">Select booking type</option>
                  <option value="Walk-in (approach us)">Walk-in (approach us)</option>
                  <option value="Walk-in (approach by team)">Walk-in (approach by team)</option>
                  <option value="Online Booking">Online Booking</option>
                  <option value="Brought by Guide">Brought by Guide</option>
                  <option value="In-house">In-house</option>
                  <option value="Restaurant">Restaurant</option>
                  <option value="OTA">OTA</option>
                  <option value="DMC">DMC</option>
                  <option value="Other (Shops/Restaurants)">Other (Shops/Restaurants)</option>
                </select>
                {errors.bookingType && (
                  <p className="text-xs text-red-600 mt-1">{errors.bookingType}</p>
                )}
              </div>
            </div>

            {/* Guide Selection (Conditional) */}
            {bookingType === "Brought by Guide" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Guide <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedGuideId}
                  onChange={(e) => {
                    if (e.target.value === "add") navigate("/add-guide");
                    else setSelectedGuideId(e.target.value);
                  }}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                >
                  <option value="">Select a guide</option>
                  {guideList.map((guide) => (
                    <option key={guide._id} value={guide._id}>
                      {guide.guideName} ({guide.guideId})
                    </option>
                  ))}
                  <option value="add" className="text-blue-600">➕ Add New Guide</option>
                </select>
                {errors.guide && (
                  <p className="text-xs text-red-600 mt-1">{errors.guide}</p>
                )}
              </div>
            )}

            {/* Second Row - Pricing */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ticket Price</label>
                <select
                  value={isCustomPrice ? "custom" : ticketUnitPrice}
                  onChange={handlePriceChange}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                >
                  {/* <option value={3000}>LKR 3,000 (60% Discount)</option> */}
                  <option value={5000}>LKR 5,000 </option>
                  <option value="custom">Custom Price</option>
                </select>
              </div>

              {/* Custom Price Input (Conditional) */}
              {isCustomPrice && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Custom Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customPrice}
                    onChange={handleCustomPriceChange}
                    placeholder="Enter custom price"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                  />
                  {errors.customPrice && (
                    <p className="text-xs text-red-600 mt-1">{errors.customPrice}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Number of Tickets <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={numberOfTickets}
                  onChange={handleTicketChange}
                  placeholder="Enter quantity"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                />
                {errors.numberOfTickets && (
                  <p className="text-xs text-red-600 mt-1">{errors.numberOfTickets}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Total Price</label>
                <div className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm bg-slate-50 text-slate-600">
                  LKR {totalPrice.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Third Row - Payment & Customer */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                >
                  <option value="">Select payment method</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                </select>
                {errors.paymentMethod && (
                  <p className="text-xs text-red-600 mt-1">{errors.paymentMethod}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Customer Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={customerType}
                  onChange={(e) => setCustomerType(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                >
                  <option value="">Select customer type</option>
                  <option value="Foreigner">Foreigner</option>
                  <option value="Local">Local</option>
                </select>
                {errors.customerType && (
                  <p className="text-xs text-red-600 mt-1">{errors.customerType}</p>
                )}
              </div>
            </div>

            {/* Fourth Row - Invoice Number */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Invoice Number <span className="text-slate-400"></span>
                </label>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Enter invoice number"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100"
                />
                {errors.invoiceNumber && (
                  <p className="text-xs text-red-600 mt-1">{errors.invoiceNumber}</p>
                )}
              </div>
            </div>

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Comments {isCustomPrice && <span className="text-red-500">*</span>}
                {!isCustomPrice && <span className="text-slate-400">(Optional)</span>}
              </label>
              <textarea
                rows="3"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={isCustomPrice ? "Please explain the reason for custom pricing..." : "Enter any additional comments..."}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100 resize-vertical"
              />
              {errors.comments && (
                <p className="text-xs text-red-600 mt-1">{errors.comments}</p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                {comments.length} characters
                {isCustomPrice && " • Explanation required for custom pricing"}
              </p>
            </div>

            {/* Submit Button */}
            <div className="pt-4 border-t border-slate-200">
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-md border border-blue-500 bg-blue-500 text-white hover:bg-blue-600 hover:border-blue-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting Sale...
                  </>
                ) : (
                  "Submit Sale"
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer Info */}
        <div className="text-center mt-6">
          <p className="text-xs text-slate-400">
            All sales are recorded with timestamp and assigned to your site
          </p>
        </div>
      </div>

      {/* Team Floating Navigation */}
      <TeamFloatingNavigation />

      {/* Duplicate Sale Warning Modal */}
      {showDuplicateModal && duplicateWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-lg max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Potential Duplicate Sale Detected
              </h3>
              
              <div className="text-sm text-slate-600 mb-4 space-y-2">
                <p className="mb-3">A similar sale was recorded recently:</p>
                
                <div className="bg-slate-50 rounded-lg p-3 text-left space-y-1">
                  <div className="flex justify-between">
                    <span className="font-medium">Agent:</span>
                    <span>{duplicateWarning.salesAgent}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Tickets:</span>
                    <span>{duplicateWarning.numberOfTickets}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Total:</span>
                    <span>LKR {duplicateWarning.totalPrice?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Time:</span>
                    <span>{duplicateWarning.timeAgo}</span>
                  </div>
                  {duplicateWarning.guideName && (
                    <div className="flex justify-between">
                      <span className="font-medium">Guide:</span>
                      <span>{duplicateWarning.guideName}</span>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-slate-500 mt-3">
                  Are you sure you want to submit this sale?
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDuplicateCancel}
                className="flex-1 px-4 py-2.5 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-all duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleForceSubmit}
                className="flex-1 px-4 py-2.5 text-sm font-medium rounded-md border border-amber-500 bg-amber-500 text-white hover:bg-amber-600 transition-all duration-150"
              >
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-lg max-w-md w-full">
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Sale Submitted Successfully!
              </h3>
              
              <p className="text-sm text-slate-600 mb-6">
                {modalMessage}
              </p>

              <button
                onClick={handleSuccessClose}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-md border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 transition-all duration-150"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-lg max-w-md w-full">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Submission Failed
              </h3>
              
              <p className="text-sm text-slate-600 mb-6">
                {modalMessage}
              </p>

              <button
                onClick={handleErrorClose}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-md border border-red-500 bg-red-500 text-white hover:bg-red-600 transition-all duration-150"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddSalePage;
